/**
 * @fileoverview 特集の一覧等に表示する特集用カードレイアウトコンポーネント
 */
<script lang="ts">
import { defineComponent, inject } from 'vue'
import { defaultProps, defaultFunction } from '@/components/_parials/contentsCard'
import PublisherName from '@/components/contents/PublisherName.vue'
import DateDefault from '@/components/date/DateDefault.vue'
import { DATE_FORMAT_JA_WITH_DAY_OF_WEEK } from '@/constants/dateFormat'
import { localeDateJa } from '@/utilities/formatter'

export default defineComponent({
  name: 'StoryCard',

  components: {
    PublisherName,
    DateDefault,
  },

  props: {
    ...defaultProps,

    /**
     * 特集のテーマ（特集カテゴリー）名を表示するかどうか
     */
    hasTheme: {
      type: Boolean,
      default: false,
    },

    /**
     * 特集に紐づくレポート掲載号数を表示するかどうか
     */
    hasVol: {
      type: Boolean,
      default: false,
    },

    /**
     * 特集に紐づく item（レポート + 記事）総数を表示するかどうか
     */
    hasItemsTotal: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    // constants
    const THEMES = inject<Record<string, string>>('THEMES') || {}

    const {
      // computed
      sizeStateClass,
      linkTo,
      highlightHeading,
      highlightDescription,
      wrapperTagName,
      computedHeadingOptions,
      computedImageOptions,
      cssVariables,
    } = defaultFunction(props)

    return {
      // constants
      DATE_FORMAT_JA_WITH_DAY_OF_WEEK,
      THEMES,

      // computed
      sizeStateClass,
      linkTo,
      highlightHeading,
      highlightDescription,
      wrapperTagName,
      computedHeadingOptions,
      computedImageOptions,
      cssVariables,

      // methods
      localeDateJa,
    }
  },
})
</script>

<template>
  <component
    :is="wrapperTagName"
    v-if="item"
    :class="[
      'l-unit__storyCard',
      sizeStateClass,
    ]"
    :href="linkTo"
    :data-read="item.isRead"
    :data-horizontal-pc="horizontal.pc"
    :data-horizontal-sp="horizontal.sp"
    :data-hero="hasHero && !!item.thumbImageUrl"
    :style="cssVariables">
    <div
      v-if="highlightHeading"
      class="l-unit__storyCard--header">
      <div
        :class="[
          'o-heading__contentsCard',
          sizeStateClass,
        ]"
        :data-read="item.isRead">
        <component
          :is="computedHeadingOptions.tagName"
          class="o-heading__contentsCard--item"
          :style="{
            'max-height': computedHeadingOptions.maxHeight,
            'line-height': computedHeadingOptions.lineHeight,
            'font-weight': computedHeadingOptions.fontWeight,
            '--font-size-pc': computedHeadingOptions.fontSize.pc,
            '--font-size-sp': computedHeadingOptions.fontSize.sp,
          }"
          v-html="highlightHeading" />
      <!-- /.o-heading__contentsCard --></div>
    <!-- /.l-unit__storyCard header --></div>

    <div
      v-if="hasHero && item.thumbImageUrl"
      class="l-unit__storyCard--image">
      <img
        class="img"
        :src="item.thumbImageUrl"
        loading="lazy"
        alt="">
    <!-- /.l-unit__storyCard image --></div>

    <div
      v-if="item.description"
      class="l-unit__storyCard--description">
      <div class="o-label__description">
        <p
          class="o-label__description--item"
          v-html="highlightDescription" />
      <!-- /.o-label__description --></div>
    <!-- /.l-unit__storyCard description --></div>

    <div
      v-if="item.publisher"
      class="l-unit__storyCard--footer">
      <div class="l-cols__footer">
        <div
          v-if="item.publisher"
          class="l-cols__footer--col is-publisher">
          <PublisherName
            :image="item.publisher.iconUrl"
            :label="item.publisher.name" />
        <!-- /.l-cols__footer col --></div>
      <!-- /.l-cols__footer --></div>
    <!-- /.l-unit__storyCard footer --></div>

    <div
      v-if="hasTheme && item.themeId"
      class="l-unit__storyCard--footer is-theme">
      <div class="o-label__theme">
        <p class="o-label__theme--item">
          {{ THEMES[item.themeId] }}
        </p>
      <!-- /.o-label__theme --></div>
    <!-- /.l-unit__storyCard footer --></div>

    <div
      v-if="hasVol && item.report?.total"
      class="l-unit__storyCard--footer is-vol">
      <div class="o-label__vol">
        <p class="o-label__vol--item">
          第{{ item.report?.total }}回
        </p>
      <!-- /.o-label__vol --></div>
    <!-- /.l-unit__storyCard footer --></div>

    <div
      v-if="hasItemsTotal && (item.report?.total || item.article?.total)"
      class="l-unit__storyCard--footer is-itemsTotal">
      <div class="o-label__itemsTotal">
        <p class="o-label__itemsTotal--item">
          {{ (item.report?.total ?? 0) + (item.article?.total ?? 0) }}記事
        </p>
      <!-- /.o-label__itemsTotal --></div>
    <!-- /.l-unit__storyCard footer --></div>

    <div
      v-if="item.publishDate || item.updatedAt"
      class="l-unit__storyCard--footer is-date">
      <DateDefault
        v-if="item.publishDate"
        :date="item.publishDate"
        :format="DATE_FORMAT_JA_WITH_DAY_OF_WEEK"
        suffix="掲載" />
      <DateDefault
        v-if="item.updatedAt"
        :date="item.updatedAt"
        :format="DATE_FORMAT_JA_WITH_DAY_OF_WEEK"
        suffix="更新" />
    <!-- /.l-unit__storyCard footer --></div>
  <!-- /.l-unit__storyCard --></component>
</template>

<style lang="sass" scoped>
$icon-width: 1.4em  // same size as solution logo in SolutionName.vue
$icon-margin: 0.4em

// layout
.l-unit__storyCard
  $root: &
  text-decoration: none
  overflow: hidden
  margin-top: 1em
  +transition

  &:first-child
    margin-top: 0

  &[href]
    +mouseover
      .o-heading__contentsCard--item
        text-decoration: underline

      #{$root}--image
        opacity: 0.5

    &:has(#{$root}--footer.is-name.is-link:hover)
      .o-heading__contentsCard--item
        text-decoration: none

      #{$root}--image
        opacity: 1

    &:has(#{$root}--action.is-tag:hover)
      .o-heading__contentsCard--item
        text-decoration: none

      #{$root}--image
        opacity: 1

  & &--image
    +transition

    > .img
      height: 100%
      display: block
      object-fit: cover
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)

  & &--footer
    &.is-vol,
    &.is-itemsTotal
      min-width: 3.5em

  // Phablet 以上のとき
  +breakpoint($max: false, $min: true)
    // 縦型（PC）
    &[data-horizontal-pc="false"]
      $image-width: 238
      $image-height: 104
      $horizontal-gap: 0.5em
      --image-width-pc: #{$image-width}
      --image-height-pc: #{$image-height}
      --image-aspect-pc: calc(var(--image-height-pc) / var(--image-width-pc))
      display: grid
      grid-template-columns: auto auto 1fr
      align-items: center

      #{$root}--header,
      #{$root}--description,
      #{$root}--footer
        margin-top: 0.5em

      #{$root}--header
        grid-column: 1 / 4
        grid-row: 3 / 4

      #{$root}--image
        grid-column: 1 / 4
        grid-row: 1 / 2
        position: relative

        &::before
          content: ""
          width: 0
          display: block
          padding-top: rate(var(--image-aspect-pc))

      #{$root}--footer
        &.is-theme
          grid-column: 1 / 4
          grid-row: 4 / 5

        &.is-vol
          grid-column: 1 / 2
          grid-row: 2 / 3
          padding-right: $horizontal-gap

        &.is-itemsTotal
          grid-column: 2 / 3
          grid-row: 2 / 3
          padding-right: $horizontal-gap

        &.is-date
          grid-column: 3 / 4
          grid-row: 2 / 3

    // 横型（PC）
    &[data-horizontal-pc="true"]
      $image-width: 200
      $image-height: 100
      $margin: 16
      $image-gap: 1em
      $horizontal-gap: 0.5em
      --image-width-pc: #{$image-width}
      --image-height-pc: #{$image-height}
      $image-area-size: calc(px(var(--image-width-pc)) + #{$margin}px)
      display: grid
      grid-template-columns: auto $image-gap auto auto 1fr
      grid-template-rows: auto auto auto 1fr
      align-items: center

      &[data-hero="false"]
        grid-template-columns: auto 0 auto auto 1fr

      #{$root}--description,
      #{$root}--footer
        margin-top: 0.5em

      #{$root}--header
        grid-column: 3 / 6
        grid-row: 1 / 2

      #{$root}--image
        width: px(var(--image-width-pc))
        grid-column: 1 / 2
        grid-row: 1 / 5
        position: relative

        &::before
          content: ""
          width: 0
          display: block
          padding-top: px(var(--image-height-pc))

      #{$root}--footer
        &.is-theme
          grid-column: 3 / 6
          grid-row: 2 / 3

        &.is-vol
          grid-column: 3 / 4
          grid-row: 3 / 4
          padding-right: $horizontal-gap

        &.is-itemsTotal
          grid-column: 4 / 5
          grid-row: 3 / 4
          padding-right: $horizontal-gap

        &.is-date
          grid-column: 5 / 6
          grid-row: 3 / 4

  +breakpoint
    #{$root}--description
      margin-top: 0.25em

    // 縦型（SP）
    &[data-horizontal-sp="false"]
      $image-width: 400
      $image-height: 200
      $horizontal-gap: 0.5em
      --image-width-sp: #{$image-width}
      --image-height-sp: #{$image-height}
      --image-aspect-sp: calc(var(--image-height-sp) / var(--image-width-sp))
      display: grid
      grid-template-columns: auto auto 1fr
      align-items: center

      #{$root}--header,
      #{$root}--description,
      #{$root}--footer
        margin-top: 0.5em

      #{$root}--header
        grid-column: 1 / 4
        grid-row: 3 / 4

      #{$root}--image
        grid-column: 1 / 4
        grid-row: 1 / 2
        position: relative

        &::before
          content: ""
          width: 0
          display: block
          padding-top: 50%

      #{$root}--footer
        &.is-theme
          grid-column: 1 / 4
          grid-row: 4 / 5

        &.is-vol
          grid-column: 1 / 2
          grid-row: 2 / 3
          padding-right: $horizontal-gap

        &.is-itemsTotal
          grid-column: 2 / 3
          grid-row: 2 / 3
          padding-right: $horizontal-gap

        &.is-date
          grid-column: 3 / 4
          grid-row: 2 / 3

    // 横型（SP）
    &[data-horizontal-sp="true"]
      $image-width: 136
      $image-height: 72
      $margin: 16
      $image-gap: 1em
      $horizontal-gap: 0.5em
      --image-width-sp: #{$image-width}
      --image-height-sp: #{$image-height}
      $image-area-size: calc(px(var(--image-width-sp)) + #{$margin}px)
      display: grid
      grid-template-columns: auto $image-gap auto auto 1fr
      grid-template-rows: auto auto auto 1fr
      align-items: center

      &[data-hero="false"]
        grid-template-columns: auto 0 auto auto 1fr

      #{$root}--description,
      #{$root}--footer
        margin-top: 0.5em

      #{$root}--header
        grid-column: 3 / 6
        grid-row: 1 / 2

      #{$root}--image
        width: px(var(--image-width-sp))
        grid-column: 1 / 2
        grid-row: 1 / 5
        position: relative

        &::before
          content: ""
          width: 0
          display: block
          padding-top: px(var(--image-height-sp))

      #{$root}--footer
        &.is-theme
          grid-column: 3 / 6
          grid-row: 2 / 3

        &.is-vol
          grid-column: 3 / 4
          grid-row: 3 / 4
          padding-right: $horizontal-gap

        &.is-itemsTotal
          grid-column: 4 / 5
          grid-row: 3 / 4
          padding-right: $horizontal-gap

        &.is-date
          grid-column: 5 / 6
          grid-row: 3 / 4

.l-cols__footer
  $root: &
  display: flex
  align-items: center

  & &--col
    margin-left: 1em

    &:first-child
      margin-left: 0

    &.is-publisher
      min-width: 0  // for text-overflow in publisher label

// object
.o-heading__contentsCard
  $root: &
  --font-size-pc: $font-large
  --font-size-sp: $font-large

  & &--item
    overflow: hidden
    color: $font-black
    font-size: var(--font-size-pc)

  &[data-read="true"]
    #{$root}--item
      color: $font-light-gray

  +breakpoint
    #{$root}--item
      font-size: var(--font-size-sp)

.o-label__description
  $line-height: 1.6
  $max-line: 2
  & &--item
    max-height: #{$max-line * $line-height}em
    overflow: hidden
    line-height: $line-height
    color: $font-light-gray
    font-size: $font-small

.o-label__totalComments
  $root: &

  & &--item
    display: flex
    align-items: center
    line-height: 1
    color: $font-gray
    font-size: $font-x-small

  & &--icon
    width: $icon-width
    position: relative
    top: 0.05em
    margin-right: $icon-margin

.o-label__theme
  & &--item
    line-height: 1
    color: $font-gray
    font-size: $font-x-small

.o-label__itemsTotal,
.o-label__vol
  padding: 0.2em
  background-color: rgba($bg-white, 1)
  border: 1px solid $border-orange

  & &--item
    line-height: 1
    color: $font-orange
    font-size: $font-x-small
    text-align: center
</style>
